import {
  Autocomplete,
  AutocompleteItem,
  Card,
  CardBody,
  CardHeader,
  Chip,
  Spinner
} from '@nextui-org/react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { Button } from '~/components/common/Button'
import {
  BillingAccountData,
  DataFootprintResponse,
  S3PrefixFootprint,
  S3ScanStatus,
  TaskCmdTypeCount,
  getBillingAccounts,
  getDataFootprint,
  getS3ScanStatus,
  runS3FootprintScan
} from '~/services/superAdminService'
import { BetterToast } from '~/utils/toast'

// T-shirt size buckets used by Salesforce / SIG security questionnaires.
// Returns the smallest bucket >= n (e.g. 1234 -> "10K"). The questionnaire
// expects an order of magnitude, not a precise number, so this is the
// answer the customer's security team actually wants to read off.
const ORDER_OF_MAGNITUDE_BUCKETS: { threshold: number; label: string }[] = [
  { threshold: 10, label: '10' },
  { threshold: 100, label: '100' },
  { threshold: 1_000, label: '1K' },
  { threshold: 10_000, label: '10K' },
  { threshold: 100_000, label: '100K' },
  { threshold: 1_000_000, label: '1M' },
  { threshold: 10_000_000, label: '10M' },
  { threshold: 100_000_000, label: '100M' },
  { threshold: 1_000_000_000, label: '1B' }
]

function bucketFor(n: number): string {
  if (n <= 0) return '0'
  for (const { threshold, label } of ORDER_OF_MAGNITUDE_BUCKETS) {
    if (n <= threshold) return label
  }
  return '1B+'
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat('en-US').format(n)
}

function formatBytes(bytes: number): string {
  if (bytes <= 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const exp = Math.min(Math.floor(Math.log10(bytes) / 3), units.length - 1)
  const value = bytes / Math.pow(1000, exp)
  return `${value.toFixed(value < 10 ? 2 : 1)} ${units[exp]}`
}

interface MetricCardProps {
  label: string
  value: number
  hint?: string
  showBucket?: boolean
}

function MetricCard({ label, value, hint, showBucket = true }: MetricCardProps) {
  return (
    <div className='flex flex-col gap-1 px-4 py-3 bg-tokens-neutral-bg-white rounded-lg border border-tokens-neutral-border-2'>
      <span className='text-[11px] font-medium text-default-400 uppercase tracking-wide'>
        {label}
      </span>
      <div className='flex items-baseline gap-2'>
        <span className='text-2xl font-bold text-default-900'>{formatNumber(value)}</span>
        {showBucket && (
          <Chip size='sm' variant='flat' color='primary'>
            ~{bucketFor(value)}
          </Chip>
        )}
      </div>
      {hint && <span className='text-[11px] text-default-400'>{hint}</span>}
    </div>
  )
}

function buildQuestionnaireAnswer(d: DataFootprintResponse): string {
  const lines = [
    `Customer: ${d.billing_name ?? `billing #${d.billing_id}`}`,
    `Workspaces: ${formatNumber(d.org_count)} (~${bucketFor(d.org_count)})`,
    `Users with access: ${formatNumber(d.user_count)} (~${bucketFor(d.user_count)})`,
    `Database records (conversations + messages + tasks + usage events): ${formatNumber(
      d.conversation_count + d.message_count + d.task_count + d.usage_event_count
    )} (~${bucketFor(
      d.conversation_count + d.message_count + d.task_count + d.usage_event_count
    )})`,
    `Transactions (last 365 days): ${formatNumber(d.usage_events_trailing_365d)} (~${bucketFor(
      d.usage_events_trailing_365d
    )})`,
    `Files in scope: ${formatNumber(
      d.s3_total_object_count
    )} (~${bucketFor(d.s3_total_object_count)}), ${formatBytes(d.s3_total_bytes)}`,
    `Activity window: ${d.earliest_activity ?? 'n/a'} → ${d.latest_activity ?? 'n/a'}`
  ]
  if (d.s3_has_pending_inventory) {
    const pendingPrefixes = d.s3_prefixes
      .filter(p => p.inventory_pending)
      .map(p => p.prefix)
      .join(', ')
    lines.push(
      `Note: file count is partial. Prefixes still pending a full bucket scan: ${pendingPrefixes}. Trigger "Run full S3 scan" in the super-admin tab to backfill.`
    )
  }
  return lines.join('\n')
}

export default function DataFootprint() {
  const [searchParams, setSearchParams] = useSearchParams()

  const [accounts, setAccounts] = useState<BillingAccountData[]>([])
  const [accountsLoading, setAccountsLoading] = useState(false)

  const initialId = (() => {
    const raw = searchParams.get('billingId')
    if (!raw) return null
    const parsed = parseInt(raw, 10)
    return Number.isFinite(parsed) ? parsed : null
  })()

  const [selectedId, setSelectedId] = useState<number | null>(initialId)
  const [footprint, setFootprint] = useState<DataFootprintResponse | null>(null)
  const [footprintLoading, setFootprintLoading] = useState(false)
  const [scanStatus, setScanStatus] = useState<S3ScanStatus | null>(null)
  const pollHandleRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const selectedIdRef = useRef<number | null>(initialId)
  const lastSnapshotAtRef = useRef<string | null>(null)
  // Keep refs in sync so the polling loop, started once, can read fresh
  // values without restarting whenever a different account is picked.
  useEffect(() => {
    selectedIdRef.current = selectedId
  }, [selectedId])
  useEffect(() => {
    lastSnapshotAtRef.current = scanStatus?.scanned_at ?? null
  }, [scanStatus?.scanned_at])

  // Poll the scan status while a scan is in progress. Started by
  // handleRunScan after a successful kickoff, and also on mount if the
  // initial status load reports a scan running from a previous session.
  // Stops itself once status.in_progress flips false; at that boundary,
  // refreshes the currently-selected billing account's footprint so the
  // newly-scanned numbers replace the "scan pending" placeholders.
  const startPolling = () => {
    if (pollHandleRef.current != null) return
    const tick = async () => {
      try {
        const status = await getS3ScanStatus()
        setScanStatus(status)
        const justFinished = !status.in_progress
        if (justFinished) {
          if (pollHandleRef.current != null) {
            clearInterval(pollHandleRef.current)
            pollHandleRef.current = null
          }
          // Snapshot timestamp changes when a scan completes successfully.
          // Only refresh on actual change so we don't thrash the footprint
          // endpoint when the poll happened to land on an idle status.
          const snapshotChanged =
            status.scanned_at != null && status.scanned_at !== lastSnapshotAtRef.current
          if (snapshotChanged && selectedIdRef.current != null) {
            try {
              const data = await getDataFootprint(selectedIdRef.current)
              setFootprint(data)
            } catch (err: unknown) {
              const message = err instanceof Error ? err.message : 'Unknown error'
              BetterToast.error(`Could not refresh footprint after scan. ${message}`)
            }
          }
          if (snapshotChanged) {
            BetterToast.success(
              `S3 scan complete: ${status.total_objects_scanned ?? 0} objects scanned.`
            )
          } else if (status.progress?.error) {
            BetterToast.error(`S3 scan failed: ${status.progress.error}`)
          }
        }
      } catch {
        // Polling is best-effort; transient network blips shouldn't kill
        // the loop or pop a toast each tick.
      }
    }
    pollHandleRef.current = setInterval(() => {
      void tick()
    }, 5000)
    // Tick once immediately so the UI updates before the first interval.
    void tick()
  }

  useEffect(() => {
    let cancelled = false
    getS3ScanStatus()
      .then(status => {
        if (cancelled) return
        setScanStatus(status)
        if (status.in_progress) startPolling()
      })
      .catch(() => {
        // Status is best-effort; surface failures via the scan button only.
      })
    return () => {
      cancelled = true
      if (pollHandleRef.current != null) {
        clearInterval(pollHandleRef.current)
        pollHandleRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let cancelled = false
    setAccountsLoading(true)
    getBillingAccounts()
      .then(rows => {
        if (cancelled) return
        const sorted = [...rows].sort((a, b) => (a.name || '').localeCompare(b.name || ''))
        setAccounts(sorted)
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : 'Unknown error'
        BetterToast.error(`Could not load billing accounts. ${message}`)
      })
      .finally(() => {
        if (!cancelled) setAccountsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (selectedId == null) {
      setFootprint(null)
      return
    }
    let cancelled = false
    setFootprintLoading(true)
    setFootprint(null)
    getDataFootprint(selectedId)
      .then(data => {
        if (!cancelled) setFootprint(data)
      })
      .catch((err: unknown) => {
        if (cancelled) return
        const message = err instanceof Error ? err.message : 'Unknown error'
        BetterToast.error(`Could not load data footprint. ${message}`)
      })
      .finally(() => {
        if (!cancelled) setFootprintLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [selectedId])

  const handleSelect = (key: React.Key | null) => {
    if (key === null) {
      setSelectedId(null)
      const next = new URLSearchParams(searchParams)
      next.delete('billingId')
      setSearchParams(next, { replace: true })
      return
    }
    const id = parseInt(String(key), 10)
    if (!Number.isFinite(id)) return
    setSelectedId(id)
    const next = new URLSearchParams(searchParams)
    next.set('billingId', String(id))
    setSearchParams(next, { replace: true })
  }

  const totalDbRecords = useMemo(() => {
    if (!footprint) return 0
    return (
      footprint.conversation_count +
      footprint.message_count +
      footprint.task_count +
      footprint.usage_event_count
    )
  }, [footprint])

  const handleCopyAnswer = async () => {
    if (!footprint) return
    try {
      await navigator.clipboard.writeText(buildQuestionnaireAnswer(footprint))
      BetterToast.success('Questionnaire answer copied to clipboard.')
    } catch {
      BetterToast.error('Could not copy to clipboard.')
    }
  }

  const scanRunning = scanStatus?.in_progress ?? false

  const handleRunScan = async () => {
    if (scanRunning) return
    try {
      const result = await runS3FootprintScan()
      if (!result.started) {
        BetterToast.error(result.message ?? 'Could not start scan; one may already be running.')
        // Refresh status so the UI reflects whatever actually is running.
        try {
          const status = await getS3ScanStatus()
          setScanStatus(status)
          if (status.in_progress) startPolling()
        } catch {
          // Best-effort.
        }
        return
      }
      BetterToast.success('S3 scan started. Progress will update every few seconds.')
      // Optimistic flip — show the spinner immediately rather than waiting
      // for the first poll tick to confirm in_progress=true.
      setScanStatus(prev =>
        prev
          ? {
              ...prev,
              in_progress: true,
              progress: prev.progress ?? {
                started_at: new Date().toISOString(),
                last_updated: new Date().toISOString(),
                current_prefix: null,
                total_scanned: 0,
                error: null
              }
            }
          : prev
      )
      startPolling()
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error'
      BetterToast.error(`Could not start scan. ${message}`)
    }
  }

  const scannedAtLabel = scanStatus?.scanned_at
    ? new Date(scanStatus.scanned_at).toLocaleString()
    : 'never'

  return (
    <div className='flex flex-col gap-4'>
      <Card radius='sm' shadow='sm' className='bg-tokens-neutral-bg-white'>
        <CardHeader className='p-4 border-b border-tokens-neutral-border-2'>
          <div className='flex flex-col gap-1'>
            <h2 className='text-lg font-semibold'>Customer Data Footprint</h2>
            <p className='text-sm text-default-500'>
              Order-of-magnitude estimates of database records, files, and transactions per billing
              account. Use this to answer Salesforce / SIG / cyber-insurance security questionnaires
              that ask for breach blast radius.
            </p>
          </div>
        </CardHeader>
        <CardBody className='p-4 flex flex-col gap-4'>
          <div className='flex flex-col sm:flex-row gap-3 items-start sm:items-end'>
            <Autocomplete
              label='Billing account'
              placeholder={accountsLoading ? 'Loading…' : 'Search by name'}
              isLoading={accountsLoading}
              selectedKey={selectedId == null ? null : String(selectedId)}
              onSelectionChange={handleSelect}
              className='max-w-md'
              size='sm'
              defaultItems={accounts}
            >
              {item => (
                <AutocompleteItem
                  key={String(item.id)}
                  textValue={item.name || `Billing #${item.id}`}
                >
                  <div className='flex flex-col'>
                    <span className='text-sm font-medium'>
                      {item.name || `Billing #${item.id}`}
                    </span>
                    <span className='text-xs text-default-400'>
                      {item.billing_email || '—'}
                      {item.deleted ? ' · deleted' : ''}
                    </span>
                  </div>
                </AutocompleteItem>
              )}
            </Autocomplete>
            <Button variant='outlined' isDisabled={!footprint} onClick={handleCopyAnswer}>
              Copy questionnaire answer
            </Button>
          </div>
          {footprintLoading && (
            <div className='flex items-center gap-2 text-sm text-default-500'>
              <Spinner size='sm' /> Computing footprint…
            </div>
          )}
        </CardBody>
      </Card>

      {footprint && (
        <>
          <Card radius='sm' shadow='sm' className='bg-tokens-neutral-bg-white'>
            <CardHeader className='p-4 border-b border-tokens-neutral-border-2 flex justify-between'>
              <h3 className='text-base font-semibold'>Database records</h3>
              <span className='text-xs text-default-400'>
                Activity window:{' '}
                {footprint.earliest_activity
                  ? new Date(footprint.earliest_activity).toLocaleDateString()
                  : '—'}{' '}
                →{' '}
                {footprint.latest_activity
                  ? new Date(footprint.latest_activity).toLocaleDateString()
                  : '—'}
              </span>
            </CardHeader>
            <CardBody className='p-4'>
              <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3'>
                <MetricCard
                  label='Workspaces'
                  value={footprint.org_count}
                  hint='Organizations under this billing account'
                />
                <MetricCard
                  label='Users with access'
                  value={footprint.user_count}
                  hint='Distinct user_orgs members'
                />
                <MetricCard
                  label='Account members'
                  value={footprint.member_count}
                  hint='Billing admins'
                />
                <MetricCard
                  label='Total DB records'
                  value={totalDbRecords}
                  hint='conv + msg + task + usage_event'
                />
                <MetricCard label='Conversations' value={footprint.conversation_count} />
                <MetricCard label='Messages' value={footprint.message_count} />
                <MetricCard label='Tasks' value={footprint.task_count} />
                <MetricCard label='Usage events' value={footprint.usage_event_count} />
                <MetricCard
                  label='Transactions / yr'
                  value={footprint.usage_events_trailing_365d}
                  hint='usage_events in last 365d'
                />
              </div>
            </CardBody>
          </Card>

          {footprint.tasks_by_cmd_type.length > 0 && (
            <Card radius='sm' shadow='sm' className='bg-tokens-neutral-bg-white'>
              <CardHeader className='p-4 border-b border-tokens-neutral-border-2'>
                <h3 className='text-base font-semibold'>Tasks by command type</h3>
              </CardHeader>
              <CardBody className='p-4'>
                <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3'>
                  {footprint.tasks_by_cmd_type.map((row: TaskCmdTypeCount) => (
                    <MetricCard
                      key={row.cmd_type}
                      label={row.cmd_type}
                      value={row.count}
                      showBucket={false}
                    />
                  ))}
                </div>
              </CardBody>
            </Card>
          )}

          <Card radius='sm' shadow='sm' className='bg-tokens-neutral-bg-white'>
            <CardHeader className='p-4 border-b border-tokens-neutral-border-2 flex justify-between items-center gap-3 flex-wrap'>
              <div className='flex flex-col gap-1'>
                <h3 className='text-base font-semibold'>S3 file footprint</h3>
                <span className='text-xs text-default-400'>
                  {footprint.s3_bucket || 'no bucket configured'} · last full scan: {scannedAtLabel}
                  {scanStatus?.total_objects_scanned != null
                    ? ` (${formatNumber(scanStatus.total_objects_scanned)} objects)`
                    : ''}
                </span>
                {scanRunning && scanStatus?.progress && (
                  <span className='text-xs text-tokens-primary-2 flex items-center gap-2'>
                    <Spinner size='sm' />
                    Scanning{' '}
                    {scanStatus.progress.current_prefix ? (
                      <code className='text-[11px]'>{scanStatus.progress.current_prefix}</code>
                    ) : null}
                    · {formatNumber(scanStatus.progress.total_scanned)} objects so far
                  </span>
                )}
                {!scanRunning && scanStatus?.progress?.error && (
                  <span className='text-xs text-danger-500'>
                    Last scan failed: {scanStatus.progress.error}
                  </span>
                )}
              </div>
              <Button variant='outlined' onClick={handleRunScan} isDisabled={scanRunning}>
                {scanRunning ? 'Scan running…' : 'Run full S3 scan'}
              </Button>
            </CardHeader>
            <CardBody className='p-4 flex flex-col gap-3'>
              <div className='grid grid-cols-2 sm:grid-cols-3 gap-3'>
                <MetricCard label='Files in scope' value={footprint.s3_total_object_count} />
                <div className='flex flex-col gap-1 px-4 py-3 bg-tokens-neutral-bg-white rounded-lg border border-tokens-neutral-border-2'>
                  <span className='text-[11px] font-medium text-default-400 uppercase tracking-wide'>
                    Total bytes
                  </span>
                  <span className='text-2xl font-bold text-default-900'>
                    {formatBytes(footprint.s3_total_bytes)}
                  </span>
                </div>
              </div>
              {footprint.s3_has_pending_inventory && (
                <div className='text-xs text-default-500 bg-warning-50 border border-warning-200 rounded p-3'>
                  Some prefixes (<code>s3_upload/</code>, <code>conversations/</code>) bury{' '}
                  <code>org_id</code> below the top of the key, so they're zeroed in live requests.
                  Click <strong>Run full S3 scan</strong> above to walk the bucket once and cache
                  real per-customer counts. The cached snapshot is shared across all customers —
                  re-run when you want fresh numbers.
                </div>
              )}
              {footprint.s3_prefixes.length > 0 && (
                <div className='flex flex-col gap-1'>
                  <span className='text-[11px] font-medium text-default-400 uppercase tracking-wide'>
                    Per-prefix breakdown
                  </span>
                  <div className='flex flex-col gap-1 text-sm'>
                    {footprint.s3_prefixes.map((p: S3PrefixFootprint) => (
                      <div
                        key={p.prefix}
                        className='flex items-center justify-between border-b border-tokens-neutral-border-1 py-1'
                      >
                        <code className='text-xs'>{p.prefix}</code>
                        <div className='flex items-center gap-2'>
                          {p.inventory_pending ? (
                            <Chip size='sm' variant='flat' color='warning'>
                              scan pending
                            </Chip>
                          ) : (
                            <>
                              <span className='text-default-700'>
                                {formatNumber(p.object_count)} objects
                              </span>
                              <span className='text-default-400'>{formatBytes(p.total_bytes)}</span>
                              {p.snapshot_at && (
                                <span className='text-[11px] text-default-400'>
                                  · {new Date(p.snapshot_at).toLocaleDateString()}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardBody>
          </Card>
        </>
      )}
    </div>
  )
}
